import { FC, useMemo, useState } from 'react'

import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { useDebounce } from 'use-debounce'

import CloseIcon from 'src/Assets/Svg/pages-icons/Close.svg?react'
import { AdCampaignStrategyEnum } from 'src/Redux/ad-campaignApi'
import {
  useGetMetaAdsCampaignByIdQuery,
  useUpdateMetaAdsCampaignMutation,
} from 'src/Redux/adsCampaignApi'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { CollapsedSearchInput } from 'src/Components/CollapsedSearchInput'
import { DEBOUNCE_TIMER_300 } from 'src/Constants/constants'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { CTAButton } from 'src/Components/Buttons/CTAButton'

import { AdsLanguagesModalList } from './AdsLanguagesModalList'

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`

const ButtonsContainer = styled.div`
  position: fixed;
  bottom: 0px;
  left: 0px;
  border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  display: flex;
  width: 100%;
  padding: 8px 16px;
  justify-content: center;
  gap: 24px;
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;
`

const Title = styled.h2`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  margin: 0;
`

const SubTitle = styled(Title)`
  font-size: 14px;
  font-weight: 400;
`

const Container = styled.div`
  position: sticky;
  top: 0px;
  padding-top: 24px;
  padding-bottom: 24px;
  background-color: ${({ theme }) => theme.color.white};
`

const StyledSearchInput = styled(CollapsedSearchInput)`
  max-width: 600px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};

  &.ant-input-disabled {
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
  }

  &.ant-input-affix-wrapper .ant-input-prefix {
    margin-inline-end: 8px;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    border-radius: 40px;
    width: 100%;

    input::placeholder {
      visibility: visible;
    }
  }

  &:hover:enabled {
    background-color: ${({ theme }) => theme.color.white} !important;
  }

  &.ant-input-outlined:hover:not([disabled]) {
    background-color: ${({ theme }) => theme.color.white} !important;
  }

  &.ant-input-disabled:hover:not([disabled]) {
    background-color: ${({ theme }) => theme.color.white} !important;
  }

  &:focus-visible,
  &:focus,
  &:focus-within {
    width: 100%;

    @media screen and (max-width: 767px) {
      position: relative;
      width: 100%;

      input::placeholder {
        visibility: visible;
      }
    }
  }

  input::placeholder {
    color: ${({ theme }) => theme.color.slateGrey};
  }
`

type Props = {
  onClose: () => void
}

export const AdsLanguagesModalContent: FC<Props> = ({ onClose }) => {
  const { number } = useParams()
  const { data } = useGetMetaAdsCampaignByIdQuery(Number(number))
  const [updateMetaAdsCampaign, { isLoading: isUpdating }] =
    useUpdateMetaAdsCampaignMutation()

  const [searchValue, setSearchValue] = useState('')
  const [localSelectedLanguage, setLocalSelectedLanguage] = useState<
    string[] | undefined
  >()
  const [debouncedSearchValue] = useDebounce(searchValue, DEBOUNCE_TIMER_300)

  const selectedLanguage = useMemo(
    () => localSelectedLanguage ?? data?.languages ?? [],
    [localSelectedLanguage, data?.languages],
  )

  const headerText =
    data?.strategy === AdCampaignStrategyEnum.PLAYLIST
      ? 'Playlist language(s)'
      : 'Languages'

  const subTitle =
    data?.strategy === AdCampaignStrategyEnum.PLAYLIST
      ? 'You can select up to 2 languages for your playlist'
      : 'Choose one language that best fits your track'

  const handleClick = (item: string): void => {
    setLocalSelectedLanguage((prev) => {
      const currentLanguages = prev ?? selectedLanguage

      return currentLanguages.includes(item)
        ? currentLanguages.filter((lang) => lang !== item)
        : [...currentLanguages, item]
    })
  }

  const handleSave = (): void => {
    updateMetaAdsCampaign({
      id: Number(number),
      languages: selectedLanguage,
    })
      .unwrap()
      .finally(() => {
        onClose()
      })
  }

  return (
    <>
      <Header>
        <Title>{headerText}</Title>
        <SubTitle>{subTitle}</SubTitle>
      </Header>

      <Container>
        <StyledSearchInput
          placeholder='Enter a language name'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          allowClear={{ clearIcon: <CloseIcon /> }}
        />
      </Container>

      <AdsLanguagesModalList
        strategy={data?.strategy}
        selectedLanguage={selectedLanguage}
        searchValue={debouncedSearchValue}
        onClick={handleClick}
      />

      <ButtonsContainer translate='no'>
        <PrimaryDottedButton onClick={onClose}>Go Back</PrimaryDottedButton>
        <StyledCTAButton
          loading={isUpdating}
          disabled={!selectedLanguage.length}
          onClick={handleSave}
        >
          Add language
        </StyledCTAButton>
      </ButtonsContainer>
    </>
  )
}
